import { useMemo } from 'react';
import { MasteryRecord } from './useQuizMasteryDB';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

export interface CategoryReviewScore {
  key: string;
  subject: string;
  subcategory: string | null;
  totalQuestions: number;
  attemptedQuestions: number;
  /** 初見正答率 (0-100) — 一度も間違えずに正解した問題の割合 */
  firstAttemptCorrectRate: number;
  /** 最後に演習した日時 (ISO文字列) */
  lastStudiedAt: string | null;
  /** 最後の演習からの経過日数 */
  daysSinceLastStudy: number | null;
  /** エビングハウス忘却スコア (0-100) */
  forgettingScore: number;
  /** 初見正答率の低さスコア (0-100) */
  lowAccuracyScore: number;
  /** 総合復習優先スコア (0-100) */
  totalScore: number;
  /** 緊急度ラベル */
  urgency: 'critical' | 'high' | 'medium' | 'low';
}

function calcForgettingScore(daysSince: number): number {
  if (daysSince <= 0) return 0;
  const retention = 100 * Math.exp(-0.3 * Math.sqrt(daysSince));
  const forgetting = 100 - retention;
  return Math.min(Math.round(forgetting), 100);
}

function calcLowAccuracyScore(
  totalQuestions: number,
  attemptedQuestions: number,
  firstAttemptCorrectRate: number
): number {
  if (totalQuestions === 0) return 0;
  const unattemptedRate = ((totalQuestions - attemptedQuestions) / totalQuestions) * 100;
  const firstAttemptIncorrectRate = 100 - firstAttemptCorrectRate;
  const score = unattemptedRate * 0.6 + firstAttemptIncorrectRate * 0.4;
  return Math.min(Math.round(score), 100);
}

function calcTotalScore(forgettingScore: number, lowAccuracyScore: number, attemptedQuestions: number): number {
  if (attemptedQuestions === 0) {
    return Math.round(lowAccuracyScore * 0.5);
  }
  return Math.round(forgettingScore * 0.5 + lowAccuracyScore * 0.5);
}

function getUrgency(totalScore: number, forgettingScore: number, firstAttemptCorrectRate: number): CategoryReviewScore['urgency'] {
  if (totalScore >= 70 || (forgettingScore >= 60 && firstAttemptCorrectRate < 50)) return 'critical';
  if (totalScore >= 50 || forgettingScore >= 45) return 'high';
  if (totalScore >= 30) return 'medium';
  return 'low';
}

export function useReviewRanking(
  questions: QuizQuestion[],
  masteryRecord: MasteryRecord
): {
  ranking: CategoryReviewScore[];
  topCategories: CategoryReviewScore[];
} {
  return useMemo(() => {
    if (questions.length === 0 || Object.keys(masteryRecord).length === 0) {
      return { ranking: [], topCategories: [] };
    }

    const now = new Date();

    const categoryMap: Record<string, {
      subject: string;
      subcategory: string | null;
      questionIds: string[];
    }> = {};

    questions.forEach((q) => {
      const key = q.subcategory ? `${q.subject}>${q.subcategory}` : q.subject;
      if (!categoryMap[key]) {
        categoryMap[key] = { subject: q.subject, subcategory: q.subcategory, questionIds: [] };
      }
      categoryMap[key].questionIds.push(q.id);
    });

    const scores: CategoryReviewScore[] = [];

    Object.entries(categoryMap).forEach(([key, cat]) => {
      const { subject, subcategory, questionIds } = cat;
      const totalQuestions = questionIds.length;

      const attemptedIds = questionIds.filter((id) => {
        const r = masteryRecord[id];
        return r && (r.correct_count > 0 || r.incorrect_count > 0);
      });
      const attemptedQuestions = attemptedIds.length;

      /**
       * 初見正答率の計算ロジック（修正版）
       * ─────────────────────────────────────────
       * 「初見」とは、その問題を最初に解いたときのこと。
       * - incorrect_count === 0 かつ correct_count > 0 → 初見で正解（一度も間違えていない）
       * - incorrect_count > 0 → 初見で不正解（少なくとも1回は間違えた）
       *
       * 例: 10問中1問でも間違えた場合 → 初見正答率 = 9/10 = 90%
       * 「間違えた問題に挑戦する」で後から正解しても、
       * incorrect_count は残るため初見不正解としてカウントされる。
       */
      let firstAttemptCorrectCount = 0;
      let firstAttemptTotalCount = 0;
      let latestStudyTimestamp = 0;

      questionIds.forEach((id) => {
        const r = masteryRecord[id];
        if (!r) return;

        const correct = r.correct_count || 0;
        const incorrect = r.incorrect_count || 0;
        const total = correct + incorrect;

        if (total > 0) {
          firstAttemptTotalCount++;
          // incorrect_count が 0 かつ correct_count > 0 のみ「初見正解」
          // incorrect_count > 0 の場合は、何度正解しても「初見不正解」
          if (incorrect === 0 && correct > 0) {
            firstAttemptCorrectCount++;
          }
        }

        // 最終演習日時: updated_at を優先、なければ last_incorrect_at を使用
        const updatedAt = r.updated_at ? new Date(r.updated_at).getTime() : 0;
        const lastIncorrectAt = r.last_incorrect_at ? new Date(r.last_incorrect_at).getTime() : 0;
        const ts = Math.max(updatedAt, lastIncorrectAt);
        if (ts > latestStudyTimestamp) latestStudyTimestamp = ts;
      });

      const firstAttemptCorrectRate = firstAttemptTotalCount > 0
        ? Math.round((firstAttemptCorrectCount / firstAttemptTotalCount) * 100)
        : 0;

      const lastStudiedAt = latestStudyTimestamp > 0
        ? new Date(latestStudyTimestamp).toISOString()
        : null;

      const daysSinceLastStudy = lastStudiedAt
        ? Math.floor((now.getTime() - latestStudyTimestamp) / (1000 * 60 * 60 * 24))
        : null;

      const forgettingScore = daysSinceLastStudy !== null
        ? calcForgettingScore(daysSinceLastStudy)
        : 0;

      const lowAccuracyScore = calcLowAccuracyScore(totalQuestions, attemptedQuestions, firstAttemptCorrectRate);
      const totalScore = calcTotalScore(forgettingScore, lowAccuracyScore, attemptedQuestions);
      const urgency = getUrgency(totalScore, forgettingScore, firstAttemptCorrectRate);

      if (attemptedQuestions === 0) return;

      scores.push({
        key,
        subject,
        subcategory,
        totalQuestions,
        attemptedQuestions,
        firstAttemptCorrectRate,
        lastStudiedAt,
        daysSinceLastStudy,
        forgettingScore,
        lowAccuracyScore,
        totalScore,
        urgency,
      });
    });

    const ranking = scores.sort((a, b) => b.totalScore - a.totalScore);
    const topCategories = ranking.slice(0, 5);

    return { ranking, topCategories };
  }, [questions, masteryRecord]);
}
